import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpenCheck,
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Check,
  Sparkles,
  Lock as LockIcon,
  Copy,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_TYPES = [
  "Econômica",
  "Magna de Iniciação",
  "Magna de Elevação",
  "Magna de Exaltação",
  "Banquete Ritualístico",
  "Instrução",
];

export const BalaustreTemplatesView = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/balaustre-templates`);
      setTemplates(res.data);
      if (res.data.length && !selected) setSelected(res.data[0]);
    } catch (err) {
      toast.error("Erro ao carregar templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (tpl) => {
    if (tpl.is_default) {
      toast.error("Templates padrão não podem ser excluídos.");
      return;
    }
    if (!window.confirm(`Excluir o template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/balaustre-templates/${tpl.id}`);
      toast.success("Template excluído.");
      setSelected(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir.");
    }
  };

  const handleDuplicate = (tpl) => {
    setEditing({
      ...tpl,
      id: undefined,
      name: `${tpl.name} (Cópia)`,
      is_default: false,
    });
    setShowModal(true);
  };

  const filtered = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.session_type.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="balaustre-templates-view">
      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-cyan-400">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-cyan-400 font-bold mb-1">
              <BookOpenCheck className="w-4 h-4" />
              <span>Chancelaria • Modelos de Balaústres</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Templates Ritualísticos</h2>
            <p className="text-xs text-slate-400 mt-1">
              Modelos por tipo de sessão para agilizar a redação de Balaústres pela Chancelaria.
            </p>
          </div>
          <button
            data-testid="templates-btn-new"
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(6,182,212,0.25)]"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Template</span>
          </button>
        </div>

        <div className="mt-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="templates-input-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou tipo de sessão..."
            className="w-full bg-[#0B111E] border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-cyan-400 outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="luxury-card p-3 lg:col-span-1 max-h-[65vh] overflow-y-auto">
          {loading && <div className="text-slate-500 text-xs p-4">Carregando...</div>}
          <div className="space-y-1.5">
            {filtered.map((t) => {
              const isSel = selected?.id === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`template-item-${t.id}`}
                  onClick={() => setSelected(t)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    isSel ? "bg-cyan-500/10 border-cyan-500/60" : "bg-[#080C14] border-slate-800 hover:border-cyan-500/30"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-cyan-400 tracking-wider font-bold flex items-center space-x-1">
                        <span>{t.session_type}</span>
                        {t.is_default && <LockIcon className="w-2.5 h-2.5" />}
                      </div>
                      <div className="text-sm font-semibold text-white mt-0.5">{t.name}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <div className="luxury-card p-10 text-center">
              <Sparkles className="w-12 h-12 mx-auto text-slate-500 mb-3" />
              <p className="text-slate-400 text-sm">Selecione um template para visualizar.</p>
            </div>
          ) : (
            <div className="luxury-card p-6 border-l-4 border-l-cyan-400">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-cyan-400 font-bold">
                    {selected.session_type}
                    {selected.is_default && <span className="ml-2 text-[10px] bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 rounded-full">Padrão do sistema</span>}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mt-1">{selected.name}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="template-btn-duplicate"
                    onClick={() => handleDuplicate(selected)}
                    className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-cyan-300 border border-cyan-500/40 font-semibold px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Duplicar</span>
                  </button>
                  {!selected.is_default && (
                    <button
                      data-testid="template-btn-edit"
                      onClick={() => { setEditing(selected); setShowModal(true); }}
                      className="flex items-center space-x-1.5 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>Editar</span>
                    </button>
                  )}
                  {!selected.is_default && (
                    <button
                      data-testid="template-btn-delete"
                      onClick={() => handleDelete(selected)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-slate-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-5 bg-[#080C14] border border-slate-800 rounded-lg p-4 max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-sm text-slate-200 leading-6 font-mono">
                {selected.content}
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <TemplateModal
          isOpen={showModal}
          initialData={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); fetchTemplates(); }}
        />
      )}
    </div>
  );
};

const TemplateModal = ({ isOpen, onClose, onSaved, initialData }) => {
  const [form, setForm] = useState({
    name: "",
    session_type: "Econômica",
    content: "",
    is_default: false,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        session_type: initialData.session_type || "Econômica",
        content: initialData.content || "",
        is_default: false,
      });
    } else {
      setForm({ name: "", session_type: "Econômica", content: "", is_default: false });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (initialData?.id) {
        await axios.put(`${API}/balaustre-templates/${initialData.id}`, form);
        toast.success("Template atualizado.");
      } else {
        await axios.post(`${API}/balaustre-templates`, form);
        toast.success("Template criado.");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar template.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="template-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] bg-[#0B111E] border border-cyan-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-800 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold flex items-center space-x-2">
              <BookOpenCheck className="w-3.5 h-3.5" />
              <span>Chancelaria • {initialData?.id ? "Editar" : "Novo"} Template</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              {initialData?.name || "Novo modelo ritualístico"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Nome do modelo *</label>
              <input
                data-testid="template-input-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Tipo de sessão *</label>
              <select
                data-testid="template-input-session-type"
                required
                value={form.session_type}
                onChange={(e) => setForm({ ...form, session_type: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400 outline-none"
              >
                {SESSION_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Conteúdo do template *</label>
            <textarea
              data-testid="template-input-content"
              required
              rows={16}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-400 outline-none font-mono leading-6 resize-none"
              placeholder="Digite o texto ritualístico com placeholders (___) que a Chancelaria poderá preencher..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold px-4"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="template-btn-submit"
              className="flex items-center space-x-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
            >
              <Check className="w-3.5 h-3.5" />
              <span>{initialData?.id ? "Atualizar" : "Salvar Template"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
